import { AlertTriangle, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import type { AuthContextType } from '@/contexts/AuthContext'

export const AUTH_BOOT_TIMEOUT_MS = 10_000
const E2E_TIMEOUT_OVERRIDE_KEY = 'doneverse.e2e.auth_boot_timeout_ms'

type AuthBootstrapState = Pick<
  AuthContextType,
  'isAuthenticated' | 'isLoggingOut' | 'loading' | 'logout' | 'user'
>

export function AuthBootstrapBoundary({
  auth,
  children,
  timeoutMs = AUTH_BOOT_TIMEOUT_MS,
}: {
  auth: AuthBootstrapState
  children: React.ReactNode
  timeoutMs?: number
}) {
  const resolvedTimeoutMs =
    import.meta.env.VITE_E2E_TEST_MODE === 'true'
      ? Number(localStorage.getItem(E2E_TIMEOUT_OVERRIDE_KEY)) || timeoutMs
      : timeoutMs
  const [timedOut, setTimedOut] = useState(false)
  const shouldTrackTimeout = auth.loading && !auth.isLoggingOut
  const canSignOut = auth.isAuthenticated || !!auth.user

  useEffect(() => {
    if (!shouldTrackTimeout) {
      setTimedOut(false)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setTimedOut(true)
    }, resolvedTimeoutMs)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [resolvedTimeoutMs, shouldTrackTimeout])

  if (!shouldTrackTimeout) {
    return <>{children}</>
  }

  if (!timedOut) {
    return (
      <div className="flex justify-center items-center h-dvh" data-testid="auth-bootstrap-loading">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex flex-col justify-center items-center h-dvh gap-4 p-4">
      <AlertTriangle className="h-12 w-12 text-destructive" />
      <h1 className="text-xl font-semibold">Authentication is taking longer than expected</h1>
      <p className="text-muted-foreground text-center max-w-md">
        {canSignOut
          ? 'We could not finish verifying your session. Sign out and try again.'
          : 'We could not complete authentication. Reload and try again.'}
      </p>
      <div className="flex gap-3 mt-2">
        {canSignOut ? <Button onClick={() => void auth.logout().catch(() => {})}>Sign Out</Button> : null}
        <Button variant={canSignOut ? 'outline' : 'default'} onClick={() => window.location.reload()}>
          Reload
        </Button>
      </div>
    </div>
  )
}
